/**
 * MikroAnim — Phaser-tween-presets for korte, polerte mikro-animasjoner.
 *
 * Bygd i runde 43 for å heve følt produksjonskvalitet på de små
 * detaljene: salgs-milestone, hover-tilbakemelding, fase-overgang.
 *
 * Alle presets respekterer `foretrekkerRedusertBevegelse()` og blir til
 * no-ops når brukeren har skrudd på reduced motion.
 *
 * Hver helper tar et Phaser-game-objekt (eller liste) og en valgfri
 * konfigurasjon. De returnerer ingenting, men starter en tween på den
 * gitte scene-instansen.
 */

import type Phaser from 'phaser';
import { foretrekkerRedusertBevegelse } from '@/systems/motionPreferanser';

type Target = Phaser.GameObjects.GameObject & {
  x?: number;
  y?: number;
  scaleX?: number;
  scaleY?: number;
  scale?: number;
  alpha?: number;
  angle?: number;
  rotation?: number;
  setTint?: (tint: number) => unknown;
  clearTint?: () => unknown;
  setFillStyle?: (color: number, alpha?: number) => unknown;
};

function hentScene(target: Target): Phaser.Scene | undefined {
  const obj = target as unknown as { scene?: Phaser.Scene };
  return obj.scene;
}

export interface ShakeValg {
  /** Antall piksler maks utslag. Default: 4. */
  intensitet?: number;
  /** Antall steg i shake-syklus. Default: 5. */
  steg?: number;
  /** Total varighet i ms. Default: 250. */
  varighet?: number;
}

export function shake(target: Target, valg: ShakeValg = {}): void {
  if (foretrekkerRedusertBevegelse()) return;
  const scene = hentScene(target);
  if (!scene) return;
  const intensitet = valg.intensitet ?? 4;
  const steg = valg.steg ?? 5;
  const varighet = valg.varighet ?? 250;
  const stegMs = Math.max(20, Math.round(varighet / (steg * 2)));
  const origX = (target as { x?: number }).x ?? 0;

  for (let i = 0; i < steg; i += 1) {
    const dx = (i % 2 === 0 ? 1 : -1) * intensitet;
    scene.tweens.add({
      targets: target,
      x: origX + dx,
      duration: stegMs,
      delay: i * stegMs,
      ease: 'Sine.easeInOut',
    });
  }
  scene.tweens.add({
    targets: target,
    x: origX,
    duration: stegMs,
    delay: steg * stegMs,
    ease: 'Sine.easeOut',
  });
}

export interface BounceInValg {
  /** Maks overshoot-scale. Default: 1.2. */
  toppScale?: number;
  /** Total varighet i ms. Default: 400. */
  varighet?: number;
  /** Sluttscale (vanligvis 1). Default: 1. */
  sluttScale?: number;
}

export function bounceIn(target: Target, valg: BounceInValg = {}): void {
  const scene = hentScene(target);
  if (!scene) return;
  const topp = valg.toppScale ?? 1.2;
  const slutt = valg.sluttScale ?? 1;
  const varighet = valg.varighet ?? 400;
  if (foretrekkerRedusertBevegelse()) {
    (target as { setScale?: (s: number) => unknown }).setScale?.(slutt);
    return;
  }

  (target as { setScale?: (s: number) => unknown }).setScale?.(0);
  scene.tweens.add({
    targets: target,
    scale: topp,
    duration: Math.round(varighet * 0.6),
    ease: 'Back.easeOut',
    onComplete: () => {
      scene.tweens.add({
        targets: target,
        scale: slutt,
        duration: Math.round(varighet * 0.4),
        ease: 'Sine.easeOut',
      });
    },
  });
}

export interface FlashValg {
  /** Phaser-fargenummer (eks 0xb8860b). Default: 0xffffff. */
  farge?: number;
  /** Varighet for fade inn + ut. Default: 300. */
  varighet?: number;
  /** Topp-alpha for flash. Default: 0.6. */
  toppAlpha?: number;
}

export function flash(target: Target, valg: FlashValg = {}): void {
  if (foretrekkerRedusertBevegelse()) return;
  const scene = hentScene(target);
  if (!scene) return;
  const farge = valg.farge ?? 0xffffff;
  const varighet = valg.varighet ?? 300;
  const toppAlpha = valg.toppAlpha ?? 0.6;

  // To strategier: tint hvis støttet, ellers fade alpha
  const tintMetode = (target as { setTint?: (t: number) => unknown }).setTint;
  const clearTintMetode = (target as { clearTint?: () => unknown }).clearTint;

  if (typeof tintMetode === 'function' && typeof clearTintMetode === 'function') {
    tintMetode.call(target, farge);
    scene.time.delayedCall(Math.round(varighet * 0.5), () => {
      try {
        clearTintMetode.call(target);
      } catch {
        // ignore
      }
    });
    return;
  }

  const origAlpha = (target as { alpha?: number }).alpha ?? 1;
  scene.tweens.add({
    targets: target,
    alpha: toppAlpha,
    duration: Math.round(varighet * 0.4),
    ease: 'Sine.easeOut',
    yoyo: true,
    onComplete: () => {
      (target as { alpha?: number }).alpha = origAlpha;
    },
  });
}

export interface WiggleValg {
  /** Maks rotasjon i grader. Default: 5. */
  grader?: number;
  /** Antall vugger. Default: 3. */
  antall?: number;
  /** Total varighet i ms. Default: 400. */
  varighet?: number;
}

export function wiggle(target: Target, valg: WiggleValg = {}): void {
  if (foretrekkerRedusertBevegelse()) return;
  const scene = hentScene(target);
  if (!scene) return;
  const grader = valg.grader ?? 5;
  const antall = valg.antall ?? 3;
  const varighet = valg.varighet ?? 400;
  const stegMs = Math.max(40, Math.round(varighet / (antall * 2)));
  const origAngle = (target as { angle?: number }).angle ?? 0;

  for (let i = 0; i < antall; i += 1) {
    const retning = i % 2 === 0 ? 1 : -1;
    scene.tweens.add({
      targets: target,
      angle: origAngle + retning * grader,
      duration: stegMs,
      delay: i * stegMs,
      ease: 'Sine.easeInOut',
    });
  }
  scene.tweens.add({
    targets: target,
    angle: origAngle,
    duration: stegMs,
    delay: antall * stegMs,
    ease: 'Sine.easeOut',
  });
}

export interface GlowValg {
  /** Phaser-farge for glow-tint. Default: 0xb8860b (gull). */
  farge?: number;
  /** Total varighet i ms. Default: 1200. */
  varighet?: number;
  /** Min-alpha i puls. Default: 0.6. */
  minAlpha?: number;
  /** Antall pulser. Default: 3. */
  pulser?: number;
}

export function glow(target: Target, valg: GlowValg = {}): void {
  if (foretrekkerRedusertBevegelse()) return;
  const scene = hentScene(target);
  if (!scene) return;
  const farge = valg.farge ?? 0xb8860b;
  const varighet = valg.varighet ?? 1200;
  const minAlpha = valg.minAlpha ?? 0.6;
  const pulser = valg.pulser ?? 3;

  const tintMetode = (target as { setTint?: (t: number) => unknown }).setTint;
  const clearTintMetode = (target as { clearTint?: () => unknown }).clearTint;
  const harTint = typeof tintMetode === 'function' && typeof clearTintMetode === 'function';

  if (harTint) {
    tintMetode.call(target, farge);
  }

  const stegMs = Math.max(80, Math.round(varighet / (pulser * 2)));
  const origAlpha = (target as { alpha?: number }).alpha ?? 1;

  scene.tweens.add({
    targets: target,
    alpha: minAlpha,
    duration: stegMs,
    yoyo: true,
    repeat: pulser - 1,
    ease: 'Sine.easeInOut',
    onComplete: () => {
      (target as { alpha?: number }).alpha = origAlpha;
      if (harTint) {
        try {
          clearTintMetode.call(target);
        } catch {
          // ignore
        }
      }
    },
  });
}

/** Bekvemmelighet — eksporter alle preset-funksjoner som et objekt. */
export const mikroAnim = {
  shake,
  bounceIn,
  flash,
  wiggle,
  glow,
};
